import React, { useState, useRef, useEffect } from 'react';
import './StoriesList.scss'
import FaSearch from "react-icons/lib/fa/search";
import roundStar from '../images/round-star.svg';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {  assignStory } from '../tutorAssignmentSlice/tutorAssignmentSlice';
import { TutorToast } from './TutorToast';
import { skylineStoryData } from '../story_data/skylineStoryData';
import skylineImage from '../images/skyline.png';
import storyImage from '../images/story-image.png';
import AssignButton from './AssignButton';
import SkillDisplay from './SkillDisplay';

function AutoWidthInput({ value, onChange, placeholder }) {
  const [inputWidth, setInputWidth] = useState(0);
  const spanRef = useRef();

  useEffect(() => {
    if (spanRef.current) {
      setInputWidth(spanRef.current.offsetWidth + 15, 40);
    }
  }, [value]);

  const changeHandler = evt => {
    onChange(evt);
  };

  return (
    <span className="auto-width-input">
      <span ref={spanRef} className="hidden-span">{value || placeholder}</span>
      <input
        className='auto-width-input-field'
        type="text"
        value={value}
        onChange={changeHandler}
        placeholder={placeholder}
        style={{
          width: inputWidth,
          padding: '0 0.4rem'
        }}
      />
    </span>
  );
};

export default function StoriesList({ assignments, roster }) {
  const [grade, setGrade] = useState('all grades');
  const [title, setTitle] = useState('any title');
  const [filteredStories, setFilteredStories] = useState(skylineStoryData.books);
  const { t } = useTranslation("tutor_assignment_modal");
  const dispatch = useDispatch();
  const selectedStudents = useSelector((state) => state.tutorAssignment.selectedStudents);

  const getStudentsWithAssignment = (storyId) => {
    const studentsWithAssignment = assignments
      .filter(assignment =>
        selectedStudents.includes(assignment.entityId) &&
        assignment.assignments?.some(a =>
          a.manifest?.some(m => m.storyId === storyId) &&
          (a.status === 'ASSIGNED' || a.status === 'IN_PROGRESS')
        )
      )
      .map(assignment => {
        const student = roster.data.student.find(s => s.id === assignment.entityId);
        return student ? `${student.first_name} ${student.last_name.charAt(0)}` : assignment.entityId;
      });

    return studentsWithAssignment;
  };

  useEffect(() => {
    const grades = new Set(roster.data.student.map(student => student.grade));

    if (grades.size === 1) {
      const gradeValue = grades.values().next().value;
      setGrade(formatGrade(gradeValue));
    } else {
      setGrade('all grades');
    }
  }, [roster]);

  const handleAssignStory = (story) => {
    if (selectedStudents.length === 0) {
      toast.custom(() => (
        <TutorToast
          message={t("please_select_students")}
          isSuccess={false}
        />
      ), { duration: 5000 });
      return;
    }

    const studentsWithAssignment = getStudentsWithAssignment(story.uuid);

    if (studentsWithAssignment.length > 0) {
      toast.custom(() => (
        <TutorToast
          message={t("this_story_has_already_been_added_to_students", {
            students: studentsWithAssignment.join(', '),
          })}
          isSuccess={false}
        />
      ), { duration: 5000 });
      return;
    }

    dispatch(assignStory({ story, selectedStudents }))
      .unwrap()
      .then((result) => {
        toast.custom(() => (
          <TutorToast
            message={t("an_assignment_has_been_added_for_number_students", {
              count: selectedStudents.length,
              number: selectedStudents.length
            })}
            isSuccess={true}
          />
        ), { duration: 5000 });
      })
      .catch((error) => {
        console.error('Assignment error:', error);
        toast.custom(() => (
          <TutorToast
            message={t("the_assignment_could_not_be_created_try_again")}
            isSuccess={false}
          />
        ), { duration: 5000 });
      });
  };

  const normalizeGradeNumber = (grade) => {
    if (grade === 'all grades') return grade;

    if (typeof grade === 'number') return grade;

    if (typeof grade === 'string') {
      const gradeText = grade.toLowerCase().trim();

      if (gradeText.includes('kindergarten') || gradeText.includes('kinder')) {
        return 0;
      } else if (gradeText.includes('first') || gradeText.includes('1st')) {
        return 1;
      } else if (gradeText.includes('second') || gradeText.includes('2nd')) {
        return 2;
      } else if (gradeText.includes('third') || gradeText.includes('3rd')) {
        return 3;
      } else if (gradeText.includes('fourth') || gradeText.includes('4th')) {
        return 4;
      } else if (gradeText.includes('fifth') || gradeText.includes('5th')) {
        return 5;
      }

      const num = Number(gradeText.replace(/(st|nd|rd|th)(\s+grade)?$/i, ''));
      return isNaN(num) ? grade : num;
    }

    return grade;
  };

  const handleSearch = (event) => {
    event.preventDefault();

    const normalizedGrades = grade
      ? grade.split(',').map(g => normalizeGradeNumber(g.trim()))
      : ['all grades'];

    const normalizedTitles = title
      ? title.split(',').map(t => t.trim().toLowerCase())
      : ['any title'];

    const isDefaultSearch =
      (normalizedGrades.includes('all grades') || normalizedGrades.length === 0) &&
      normalizedTitles.includes('any title');

    const newFilteredStories = isDefaultSearch
      ? skylineStoryData.books
      : skylineStoryData.books.filter(story => {
          const storyGrade = story.grade !== undefined ? normalizeGradeNumber(story.grade) : '';
          const matchesTitle = normalizedTitles.includes('any title') ||
            normalizedTitles.some(nt => story.title.toLowerCase().includes(nt));

          const matchesGrade = normalizedGrades.includes('all grades') ||
            normalizedGrades.some(ng => ng === storyGrade);
          return matchesTitle && matchesGrade;
        });

    setFilteredStories(newFilteredStories);
  };

  const formatGrade = (grade) => {
    if (grade === 0) return 'Kindergarten';
    if (grade === 1) return '1st Grade';
    if (grade === 2) return '2nd Grade';
    if (grade === 3) return '3rd Grade';
    if ([4, 5].includes(grade)) return `${grade}th Grade`;
    return 'all grades';
  };

  return (
        <div className='tutor-assignment-modal__stories'>
          <form className='tutor-assignment-search-bar' onSubmit={handleSearch}>
            <h3 className='tutor-assignment-search-title'>Search for Skyline Story</h3>
            <div className='tutor-assignment-search-section'>
              <div className='tutor-assignment-search-stars-container'>
                <div className='tutor-assignment-small-star'>
                  <img src={roundStar} alt='round star'/>
                </div>
                <div className='tutor-assignment-big-star'>
                  <img src={roundStar} alt='round star'/>
                </div>
              </div>
              <span className='tutor-assignment-search-input-container'>
                {t("show_all_assignable_stories_from_skyline_for")}
                <AutoWidthInput
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  placeholder="all grades"
                />
                {t("with")}
                <AutoWidthInput
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="any title"
                />
              </span>
              <button type="submit" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <FaSearch className='tutor-assignment-search-icon' size={36} />
              </button>
            </div>
          </form>
          <h4 className='tutor-assignment-results-title'>Results</h4>
          <div className='tutor-assignment-results-section'>
            <div className='tutor-assignment-story-list'>
              {filteredStories.map((story) => (
                <div key={story.uuid} className='tutor-assignment-story-item'>
                  <div className='tutor-assignment-story-image'>
                    <img className='story-image' src={storyImage} alt={story.title} />
                  </div>
                  <div className='tutor-assignment-story-title'>{story.title}</div>
                  <div className='tutor-assignment-tag-image'>
                    <img className='tag-image'src={skylineImage} alt={story.title} />
                  </div>
                  <SkillDisplay />
                  <AssignButton onAssignStoryClick={() => handleAssignStory(story)} />
                </div>
              ))}
            </div>
          </div>
        </div>
  );
}